import React, { useMemo, forwardRef } from "react";
import { Avatar, Box, Tooltip } from "@mui/material";

const AVATAR_PX = Math.round(36 * 0.8); // 20% smaller than 36
const AVATAR_FONT_REM = 0.8 * 0.8; // scale type with avatar

/** Stable hue from a string for avatar background (readable with white text). */
function colorFromNameKey(key) {
    const s = String(key || "?").toLowerCase();
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return {
        bgcolor: `hsl(${h}, 75%, 48%)`,
        color: "#fff",
    };
}

function buildInitials(firstName, lastName, email) {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f.length >= 2) return f.slice(0, 2).toUpperCase();
    if (f) return `${f[0]}`.toUpperCase();
    const em = (email || "").trim();
    if (em.length >= 2) return em.slice(0, 2).toUpperCase();
    if (em) return `${em[0]}`.toUpperCase();
    return "?";
}

/**
 * Compact table cell: MUI Avatar; hover shows full name + email.
 */
const UserAvatarTooltip = forwardRef(function UserAvatarTooltip(
    { firstName, lastName, email, displayName: displayNameProp, avatarSx = {}, ...props },
    ref
) {
    const fromParts = `${firstName || ""} ${lastName || ""}`.trim();
    const displayName = displayNameProp ?? fromParts;
    const initials = buildInitials(firstName, lastName, email);
    const hasInfo = displayName || email;
    const nameKeyForColor = (displayName || email || initials || "?").trim();
    const avatarColors = useMemo(() => colorFromNameKey(nameKeyForColor), [nameKeyForColor]);

    const title = (
        <Box sx={{ py: 0.25, maxWidth: 280 }}>
            {displayName ? (
                <Box component="div" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35 }}>
                    {displayName}
                </Box>
            ) : null}
            {email ? (
                <Box
                    component="div"
                    sx={{
                        fontSize: 12,
                        opacity: 0.92,
                        mt: displayName ? 0.5 : 0,
                        lineHeight: 1.35,
                        wordBreak: "break-all",
                    }}
                >
                    {email}
                </Box>
            ) : null}
            {!hasInfo ? (
                <Box component="div" sx={{ fontSize: 12 }}>
                    No contact details
                </Box>
            ) : null}
        </Box>
    );

    return (
        <Tooltip title={title} arrow placement="top" enterDelay={250}>
            <Avatar
                ref={ref}
                {...props}
                sx={{
                    width: AVATAR_PX,
                    height: AVATAR_PX,
                    fontSize: `${AVATAR_FONT_REM}rem`,
                    fontWeight: 400,
                    bgcolor: avatarColors.bgcolor,
                    color: avatarColors.color,
                    cursor: "default",
                    ...avatarSx,
                    ...(props.sx || {})
                }}
            >
                {initials}
            </Avatar>
        </Tooltip>
    );
});

export default UserAvatarTooltip;
